import { useMemo } from "react"
import { size, toLower } from "lodash"
import useResource from "hooks/resources"
import { ASSET_TYPES_MAP } from "helpers/assets/constants"

export function useAssetFilters(modelId, account, asset, startDate, endDate, isIntraday = false) {
  return useResource({
    resource: "assetFilters",
    url: () => {
      if (!modelId || !account || !asset || !startDate || !endDate) {
        return null
      }
      const assetType = toLower(asset.type)
      const params = { startDate, endDate, intraday: isIntraday }
      const qs = (new URLSearchParams(params)).toString()

      switch (assetType) {
        case ASSET_TYPES_MAP.SWAP:
        case ASSET_TYPES_MAP.BASKET:
          return `/api/${account}/m/${modelId}/r/swap/${asset.id}/filters?${qs}`
        case ASSET_TYPES_MAP.ETF:
          return `/api/${account}/m/${modelId}/etfs/${asset.id}/filters?${qs}`
        case ASSET_TYPES_MAP.INDEX:
          return `/api/${account}/m/${modelId}/indices/${asset.id}/filters?${qs}`
        case ASSET_TYPES_MAP.PORTFOLIO:
          return `/api/${account}/m/${modelId}/r/portfolio/${asset.id}/filters?${qs}`
        default:
          return null
      }
    },
  }, "assetFilters")
}

export function useTagCategories(instrument, account) {
  return useResource({
    resource: "tagCategories",
    url: () => {
      if (!instrument.id || !account) {
        return null
      }
      return {
        id: `tagCategories-${instrument.id}`,
        query: `query ($portfolioId: String!) {
          portfolio(id: $portfolioId) {
            tagCategories {
              id
              name
            }
          }
        }`,
        variables: { portfolioId: instrument.id },
      }
    },
  }, ["portfolio", "tagCategories"])
}

export function useAttributeCategories(instrument, account) {
  return useResource({
    resource: "attributeCategories",
    url: () => {
      if (!instrument.id || !account) {
        return null
      }
      return {
        id: `attributeCategories-${instrument.id}`,
        query: `query ($portfolioId: String!) {
          portfolio(id: $portfolioId) {
            positionAttributeCategories {
              id
              name
            }
          }
        }`,
        variables: { portfolioId: instrument.id },
      }
    },
  }, ["portfolio", "positionAttributeCategories"])
}

export function useCountryFilters(modelId, account) {
  return useResource({
    resource: "countryFilters",
    url: () => {
      if (!modelId || !account) {
        return null
      }
      return `/api/${account}/m/${modelId}/countries`
    },
  }, "countryFilters")
}

export function useCurrencyFilters(modelId, account) {
  return useResource({
    resource: "currencyFilters",
    url: () => {
      if (!modelId || !account) {
        return null
      }
      return `/api/${account}/m/${modelId}/currencies`
    },
  }, "currencyFilters")
}

export function useFilterSecurities(modelId, account, instrument, startDate, endDate, isExpanded, intraday, intradayLastRefresh) {
  const definition = useMemo(() => ({
    resource: `dashboardSecurityIds-${instrument?.type}-${instrument?.id}-${modelId}-${startDate}-${endDate}-${isExpanded}`,
    as: null,
    url: () => {
      if (!account || !instrument?.id || !startDate || !endDate || !modelId) {
        return null
      }
      const { id } = instrument
      const type = toLower(instrument.type) === "basket" ? "swap" : instrument.type

      switch (toLower(type)) {
        case "etf":
          return {
            id: `etfSecurityIds-${id}-${modelId}-${startDate}-${endDate}-${isExpanded}`,
            url: `/api/${account}/m/${modelId}/etfs/${id}/securities?${new URLSearchParams({ startDate, endDate, expand: isExpanded })}`,
          }
        case "index":
          return {
            id: `indexSecurityIds-${id}-${modelId}-${startDate}-${endDate}-${isExpanded}`,
            url: `/api/${account}/m/${modelId}/indices/${id}/securities?${new URLSearchParams({ startDate, endDate, expand: isExpanded })}`,
          }
        default:
          return {
            id: `resourceSecurityIds-${type}-${id}-${modelId}-${startDate}-${endDate}${intraday ? intradayLastRefresh : ""}-${isExpanded}`,
            url: `/api/${account}/m/${modelId}/r/${type}/${id}/securities?${new URLSearchParams({ startDate, endDate, intraday: intraday || false, expand: isExpanded || false })}`,
          }
      }
    },
  }), [account, instrument, startDate, endDate, modelId, isExpanded, intraday, intradayLastRefresh])
  return useResource(definition, "dashboardSecurityIds")
}

export function useSecuritiesDescriptors(account, modelId, securitiesIds, endDate) {
  return useResource({
    resource: `portfolioSecuritiesDescriptors-${JSON.stringify(securitiesIds)}-${modelId}-${endDate}`,
    url: () => {
      if (size(securitiesIds) === 0) {
        return null
      }

      return {
        url: `/api/${account}/m/${modelId}/securities/descriptors?date=${endDate}`,
        id: `securitiesDescriptors${account}${modelId}${endDate}${JSON.stringify(securitiesIds)}`,
        payload: { ids: securitiesIds },
      }
    },
  }, "portfolioSecuritiesDescriptors")
}
